import { ApiResponse, PaginationParams } from "../types/admin";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5167/api";

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem("admin_token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const getAuthHeadersOnly = (): HeadersInit => {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const handleUnauthorized = () => {
  localStorage.removeItem("admin_token");
  const isAdminArea = window.location.pathname.startsWith("/admin");
  if (isAdminArea && window.location.pathname !== "/admin/login") {
    window.location.href = "/admin/login";
  }
};

const isApiResponseEnvelope = <T>(data: unknown): data is ApiResponse<T> => {
  if (!data || typeof data !== "object") {
    return false;
  }

  if (!("success" in data) || typeof (data as { success?: unknown }).success !== "boolean") {
    return false;
  }

  if (!("message" in data)) {
    return true;
  }

  const message = (data as { message?: unknown }).message;
  return message === null || message === undefined || typeof message === "string";
};

const normalizeEnvelope = <T>(data: unknown): ApiResponse<T> | null => {
  if (!isApiResponseEnvelope<T>(data)) {
    return null;
  }

  const envelope = data as ApiResponse<T>;
  return {
    success: envelope.success,
    data: envelope.data,
    message: envelope.message ?? null,
  };
};

const extractResponseMessage = (data: unknown, fallback: string): string => {
  if (data && typeof data === "object" && "message" in data) {
    const message = (data as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) {
      return message;
    }
  }

  return fallback;
};

const safeParseJson = async (response: Response): Promise<unknown> => {
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    return null;
  }

  try {
    return await response.json();
  } catch {
    return null;
  }
};

const buildRequestError = <T>(error: unknown): ApiResponse<T> => {
  if (error instanceof Error && error.message.trim()) {
    return { success: false, message: error.message };
  }

  return { success: false, message: "Request failed" };
};

const processResponse = async <T>(response: Response): Promise<ApiResponse<T>> => {
  const data = response.status === 204 ? null : await safeParseJson(response);
  const envelope = normalizeEnvelope<T>(data);

  if (response.status === 401) {
    handleUnauthorized();
    if (envelope) {
      return { ...envelope, success: false, message: envelope.message || "Unauthorized" };
    }

    return { success: false, message: extractResponseMessage(data, "Unauthorized") };
  }

  if (response.status === 204) {
    return { success: true, message: null };
  }

  if (!response.ok) {
    if (envelope) {
      return {
        ...envelope,
        success: false,
        message: envelope.message || response.statusText || "Request failed",
      };
    }

    return {
      success: false,
      message: extractResponseMessage(data, response.statusText || "Request failed"),
    };
  }

  if (envelope) {
    return envelope;
  }

  return { success: true, data: data as T, message: null };
};

const buildQueryString = (params?: PaginationParams): string => {
  if (!params) return "";
  const query = new URLSearchParams();
  if (params.pageNumber !== undefined) query.set("PageNumber", String(params.pageNumber));
  if (params.pageSize !== undefined) query.set("PageSize", String(params.pageSize));
  const str = query.toString();
  return str ? `?${str}` : "";
};

const httpClient = {
  get: async <T>(path: string, pagination?: PaginationParams): Promise<ApiResponse<T>> => {
    const qs = buildQueryString(pagination);
    try {
      const response = await fetch(`${API_BASE_URL}${path}${qs}`, {
        headers: getAuthHeaders(),
      });
      return processResponse<T>(response);
    } catch (error) {
      return buildRequestError<T>(error);
    }
  },

  post: async <T>(path: string, body: unknown): Promise<ApiResponse<T>> => {
    try {
      const response = await fetch(`${API_BASE_URL}${path}`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      return processResponse<T>(response);
    } catch (error) {
      return buildRequestError<T>(error);
    }
  },

  postFormData: async <T>(path: string, formData: FormData): Promise<ApiResponse<T>> => {
    try {
      const response = await fetch(`${API_BASE_URL}${path}`, {
        method: "POST",
        headers: getAuthHeadersOnly(),
        body: formData,
      });
      return processResponse<T>(response);
    } catch (error) {
      return buildRequestError<T>(error);
    }
  },

  put: async <T>(path: string, body: unknown): Promise<ApiResponse<T>> => {
    try {
      const response = await fetch(`${API_BASE_URL}${path}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      return processResponse<T>(response);
    } catch (error) {
      return buildRequestError<T>(error);
    }
  },

  putFormData: async <T>(path: string, formData: FormData): Promise<ApiResponse<T>> => {
    try {
      const response = await fetch(`${API_BASE_URL}${path}`, {
        method: "PUT",
        headers: getAuthHeadersOnly(),
        body: formData,
      });
      return processResponse<T>(response);
    } catch (error) {
      return buildRequestError<T>(error);
    }
  },

  delete: async <T>(path: string, body?: unknown): Promise<ApiResponse<T>> => {
    try {
      const response = await fetch(`${API_BASE_URL}${path}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      return processResponse<T>(response);
    } catch (error) {
      return buildRequestError<T>(error);
    }
  },

  postPublic: async <T>(path: string, body: unknown): Promise<ApiResponse<T>> => {
    try {
      const response = await fetch(`${API_BASE_URL}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      return processResponse<T>(response);
    } catch (error) {
      return buildRequestError<T>(error);
    }
  },
};

export default httpClient;
